import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2, Upload, X, Trash2, Search, GripVertical, ArrowUp, ArrowDown, Plus, ClipboardList,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => {
  if (!u) return '';
  if (u.startsWith('http') || u.startsWith('data:')) return u;
  if (u.startsWith('/api/')) return `${API_BASE}${u}`;
  return u;
};

// Small-thumbnail variant — request `?thumb=1` for our own uploads. Falls
// back to the untouched URL for anything else (data:, remote DJ URLs, etc.).
const thumbImg = (u) => {
  if (!u) return '';
  if (u.startsWith('/api/images/')) {
    const sep = u.includes('?') ? '&' : '?';
    return `${API_BASE}${u}${sep}thumb=1`;
  }
  return resolveImg(u);
};

const FALLBACK_IMG = `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'><rect width='64' height='64' fill='#FBF0F6'/></svg>`)}`;

/**
 * Edit dialog for a custom catalog.
 * - Details tab: name, description, cover image, pricing mode + discount.
 * - Items tab: search + add products, drag/arrow reorder, remove.
 *   Order is preserved as the on-disk `item_skus` array.
 */
export default function CatalogEditDialog({ catalog, open, onOpenChange, onSaved, onConvertRequest }) {
  const [tab, setTab] = useState('details');

  // Details state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pricingMode, setPricingMode] = useState('wholesale');
  const [discountPct, setDiscountPct] = useState('');
  const [cover, setCover] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Items state
  const [items, setItems] = useState([]); // [{sku, name, brand, images, wholesale_price}]
  const [itemsLoading, setItemsLoading] = useState(false);

  // Search state
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [saving, setSaving] = useState(false);
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  // Bulk-add state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  // Reset + preload when opened
  useEffect(() => {
    if (!open || !catalog) return;
    setTab(catalog.__openTab || 'details');
    setName(catalog.name || '');
    setDescription(catalog.description || '');
    setPricingMode(catalog.pricing_mode || 'wholesale');
    setDiscountPct(String(catalog.discount_pct ?? ''));
    setCover(catalog.cover_image || null);
    setQuery('');
    setResults([]);
    // Preload expanded product data from server (keeps order).
    setItemsLoading(true);
    api.get(`/catalogs/${catalog.id}`)
      .then(({ data }) => setItems(data.products || []))
      .catch(() => toast.error('Failed to load catalog items'))
      .finally(() => setItemsLoading(false));
  }, [open, catalog]);

  // Debounced product search
  useEffect(() => {
    if (!open) return;
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get('/products', { params: { q: query.trim(), limit: 15 } });
        setResults(data.items || []);
      } catch { setResults([]); } finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open]);

  const existingSkus = useMemo(() => new Set(items.map((i) => i.sku)), [items]);

  const addItem = (product) => {
    if (existingSkus.has(product.sku)) {
      toast.info('Already in catalog');
      return;
    }
    setItems((cur) => [...cur, product]);
    setQuery('');
    setResults([]);
    toast.success(`Added ${product.name}`);
  };

  const removeItem = (sku) => {
    setItems((cur) => cur.filter((i) => i.sku !== sku));
  };

  const bulkAdd = async () => {
    // Split on newline, comma, tab, or whitespace runs. Buyers usually paste one per line.
    const raw = bulkText.split(/[\n,\t;]+/).map((s) => s.trim()).filter(Boolean);
    if (raw.length === 0) {
      toast.info('Paste at least one SKU');
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post('/products/lookup', { skus: raw });
      const found = data.found || [];
      const missing = data.missing || [];
      // Merge in order, skipping ones already in the catalog.
      const existing = new Set(items.map((i) => i.sku));
      const additions = found.filter((p) => !existing.has(p.sku));
      if (additions.length) setItems((cur) => [...cur, ...additions]);
      const dupeCount = found.length - additions.length;
      const bits = [];
      if (additions.length) bits.push(`${additions.length} added`);
      if (dupeCount) bits.push(`${dupeCount} already in catalog`);
      if (missing.length) bits.push(`${missing.length} not found (${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '…' : ''})`);
      toast[additions.length ? 'success' : 'info'](bits.join(' · ') || 'Nothing to add');
      if (additions.length) {
        setBulkText('');
        setBulkOpen(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk add failed');
    } finally { setBulkBusy(false); }
  };

  const moveItem = useCallback((from, to) => {
    if (from === to || from < 0 || to < 0) return;
    setItems((cur) => {
      if (to >= cur.length) return cur;
      const next = [...cur];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  // Native HTML5 drag/drop handlers
  const onDragStart = (idx) => setDragFrom(idx);
  const onDragOver = (idx, e) => { e.preventDefault(); if (dragOver !== idx) setDragOver(idx); };
  const onDrop = (idx, e) => {
    e.preventDefault();
    if (dragFrom !== null && dragFrom !== idx) moveItem(dragFrom, idx);
    setDragFrom(null); setDragOver(null);
  };
  const onDragEnd = () => { setDragFrom(null); setDragOver(null); };

  // Cover handlers
  const pickFile = () => fileRef.current?.click();
  const uploadCover = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await api.post(`/catalogs/${catalog.id}/cover`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCover(data.cover_image);
      toast.success('Cover uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };
  const removeCover = async () => {
    if (!cover) return;
    if (!window.confirm('Remove cover image?')) return;
    try {
      await api.delete(`/catalogs/${catalog.id}/cover`);
      setCover(null);
      toast.success('Cover removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: name.trim() || catalog.name,
        description,
        pricing_mode: pricingMode,
        discount_pct: pricingMode === 'discounted' ? Number(discountPct || 0) : 0,
        item_skus: items.map((i) => i.sku),
      };
      await api.patch(`/catalogs/${catalog.id}`, payload);
      const hasItems = items.length > 0;
      if (hasItems && onConvertRequest) {
        // One-tap prompt to spin the freshly-saved catalog into a draft order
        // for a customer. Sonner keeps this dismissible + non-blocking.
        toast.success('Catalog saved', {
          description: `${items.length} item${items.length === 1 ? '' : 's'} ready. Convert to a draft order for a customer?`,
          duration: 8000,
          action: {
            label: 'Convert to order',
            onClick: () => onConvertRequest({ ...catalog, ...payload }),
          },
        });
      } else {
        toast.success('Catalog saved');
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!catalog) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-16px)] max-w-3xl max-h-[92vh] overflow-y-auto p-4 sm:p-6 gap-3 sm:gap-4 !grid-cols-[minmax(0,1fr)]" data-testid="catalog-edit-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-xl sm:text-2xl">Edit Catalog</DialogTitle>
          <p className="sans text-[10px] sm:text-xs tracking-widest uppercase text-neutral-500">
            {items.length} product{items.length === 1 ? '' : 's'}
          </p>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="grid grid-cols-2 w-full bg-neutral-100 rounded-none h-10 p-0">
            <TabsTrigger value="details" className="rounded-none sans text-xs tracking-widest uppercase data-[state=active]:bg-white" data-testid="catalog-edit-tab-details">Details</TabsTrigger>
            <TabsTrigger value="items" className="rounded-none sans text-xs tracking-widest uppercase data-[state=active]:bg-white" data-testid="catalog-edit-tab-items">Items</TabsTrigger>
          </TabsList>

          {/* DETAILS TAB */}
          <TabsContent value="details" className="space-y-6 py-4">
            <div>
              <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="ije-input h-10 w-full"
                data-testid="edit-catalog-name"
              />
            </div>

            <div>
              <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">
                Description
                <span className="normal-case tracking-normal text-neutral-400 ml-2">(shown on the public page + PDF)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="ije-input w-full p-3 resize-none"
                placeholder="A short introduction shown at the top of the shared catalog…"
                data-testid="edit-catalog-description"
              />
            </div>

            <div>
              <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">
                Cover image
                <span className="normal-case tracking-normal text-neutral-400 ml-2">(appears on the public catalog + PDF header)</span>
              </label>
              {cover ? (
                <div className="relative border border-neutral-200 overflow-hidden max-w-md">
                  <img src={resolveImg(cover)} alt="cover" className="w-full h-40 object-cover" />
                  <button
                    type="button"
                    onClick={removeCover}
                    className="absolute top-2 right-2 bg-white/95 hover:bg-white p-1.5 border border-neutral-200 shadow-sm"
                    data-testid="edit-catalog-cover-remove"
                    title="Remove cover"
                  >
                    <Trash2 size={14} className="text-neutral-700" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={pickFile}
                  disabled={uploading}
                  className="w-full max-w-md border border-dashed border-neutral-300 hover:border-neutral-500 p-8 flex flex-col items-center gap-2 sans text-sm text-neutral-500 transition-colors"
                  data-testid="edit-catalog-cover-upload"
                >
                  {uploading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
                  <span>{uploading ? 'Uploading…' : 'Click to upload cover image'}</span>
                  <span className="text-[10px] uppercase tracking-widest text-neutral-400">JPG · PNG · WebP · Max 10MB</span>
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadCover} />
            </div>

            <div>
              <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Pricing display</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPricingMode('wholesale')}
                  className={`border p-4 text-left transition-colors ${pricingMode === 'wholesale' ? 'border-black bg-neutral-50' : 'border-neutral-200 hover:border-neutral-400'}`}
                  data-testid="pricing-mode-wholesale"
                >
                  <p className="sans text-xs tracking-widest uppercase text-neutral-900">Straight wholesale</p>
                  <p className="sans text-xs text-neutral-500 mt-1">Show the full wholesale price only.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setPricingMode('discounted')}
                  className={`border p-4 text-left transition-colors ${pricingMode === 'discounted' ? 'border-black bg-neutral-50' : 'border-neutral-200 hover:border-neutral-400'}`}
                  data-testid="pricing-mode-discounted"
                >
                  <p className="sans text-xs tracking-widest uppercase text-neutral-900">Preview discount</p>
                  <p className="sans text-xs text-neutral-500 mt-1">Strike-through wholesale + reveal a discounted &ldquo;your price&rdquo;.</p>
                </button>
              </div>
              {pricingMode === 'discounted' && (
                <div className="mt-4 max-w-xs">
                  <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Discount %</label>
                  <div className="relative">
                    <input
                      type="number" min="0" max="90" step="1"
                      value={discountPct}
                      onChange={(e) => setDiscountPct(e.target.value)}
                      className="ije-input h-10 w-full pr-8"
                      data-testid="edit-catalog-discount"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 sans text-sm text-neutral-500">%</span>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ITEMS TAB */}
          <TabsContent value="items" className="py-4 space-y-4">
            {/* Bulk add toggle */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="sans text-[10px] tracking-widest uppercase text-neutral-500">Add products</p>
              <button
                type="button"
                onClick={() => setBulkOpen((v) => !v)}
                className="sans text-[11px] tracking-widest uppercase text-neutral-500 hover:text-black inline-flex items-center gap-1"
                data-testid="bulk-add-toggle"
              >
                <ClipboardList size={13} /> {bulkOpen ? 'Close bulk paste' : 'Bulk paste SKUs'}
              </button>
            </div>

            {bulkOpen && (
              <div className="border border-neutral-200 bg-neutral-50 p-3 space-y-2" data-testid="bulk-add-panel">
                <textarea
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  rows={4}
                  placeholder={"Paste SKUs — one per line, or separated by commas / tabs.\ne.g.\n4900-04-BX\n1360-18-BX\n8160-32-BX"}
                  className="ije-input w-full p-3 resize-none text-sm font-mono"
                  data-testid="bulk-add-textarea"
                />
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <p className="sans text-[11px] text-neutral-500">
                    SKUs are matched case-insensitively. Missing ones will be reported back.
                  </p>
                  <button
                    type="button"
                    onClick={bulkAdd}
                    disabled={bulkBusy || !bulkText.trim()}
                    className="btn-primary h-10 sm:h-9 w-full sm:w-auto"
                    data-testid="bulk-add-submit"
                  >
                    {bulkBusy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add all
                  </button>
                </div>
              </div>
            )}

            {/* Search */}
            <div>
              <div className="relative">
                {!query && <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />}
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, SKU, UPC, or brand…"
                  className="ije-input h-10 w-full pl-9"
                  data-testid="catalog-items-search"
                />
                {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-neutral-400" />}
              </div>
              {results.length > 0 && (
                <ul className="mt-2 border border-neutral-200 max-h-72 overflow-y-auto divide-y divide-neutral-100" data-testid="catalog-items-search-results">
                  {results.map((p) => {
                    const inList = existingSkus.has(p.sku);
                    return (
                      <li key={p.sku}>
                        <button
                          type="button"
                          disabled={inList}
                          onClick={() => addItem(p)}
                          className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${inList ? 'opacity-40 cursor-not-allowed' : 'hover:bg-neutral-50'}`}
                          data-testid={`catalog-items-add-${p.sku}`}
                        >
                          <img
                            src={thumbImg((p.images || [])[0]) || FALLBACK_IMG}
                            alt=""
                            className="w-10 h-10 object-cover border border-neutral-200 flex-shrink-0"
                            referrerPolicy="no-referrer"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="sans text-sm text-neutral-900 truncate">{p.name}</p>
                            <p className="sans text-[11px] text-neutral-500 tracking-widest uppercase truncate">{p.brand} · {p.sku}</p>
                          </div>
                          {inList ? (
                            <span className="sans text-[10px] tracking-widest uppercase text-neutral-400">Added</span>
                          ) : (
                            <Plus size={14} className="text-neutral-500 flex-shrink-0" />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Current items list */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="sans text-[10px] tracking-widest uppercase text-neutral-500">Catalog items</p>
                <p className="sans text-[10px] tracking-widest uppercase text-neutral-400 hidden md:block">Drag to reorder</p>
              </div>
              {itemsLoading ? (
                <p className="sans text-sm text-neutral-500 py-6">Loading items…</p>
              ) : items.length === 0 ? (
                <div className="border border-dashed border-neutral-200 p-8 text-center sans text-sm text-neutral-500">
                  No products yet. Use the search above to add some.
                </div>
              ) : (
                <ul className="border border-neutral-200 divide-y divide-neutral-100" data-testid="catalog-items-list">
                  {items.map((p, i) => (
                    <li
                      key={p.sku}
                      draggable
                      onDragStart={() => onDragStart(i)}
                      onDragOver={(e) => onDragOver(i, e)}
                      onDrop={(e) => onDrop(i, e)}
                      onDragEnd={onDragEnd}
                      className={`flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 bg-white transition-colors ${dragOver === i ? 'bg-[color:var(--ije-pink-tint)]' : 'hover:bg-neutral-50'} ${dragFrom === i ? 'opacity-40' : ''}`}
                      data-testid={`catalog-item-row-${p.sku}`}
                    >
                      <GripVertical size={14} className="text-neutral-300 cursor-grab flex-shrink-0 hidden md:block" />
                      <span className="sans text-[10px] text-neutral-400 w-5 sm:w-6 text-right shrink-0">{i + 1}</span>
                      <img
                        src={thumbImg((p.images || [])[0]) || FALLBACK_IMG}
                        alt=""
                        className="w-10 h-10 object-cover border border-neutral-200 flex-shrink-0"
                        referrerPolicy="no-referrer"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="sans text-sm text-neutral-900 truncate">{p.name}</p>
                        <p className="sans text-[11px] text-neutral-500 tracking-widest uppercase truncate">{p.brand} · {p.sku}</p>
                      </div>
                      <div className="flex items-center shrink-0">
                        <button
                          type="button"
                          onClick={() => moveItem(i, i - 1)}
                          disabled={i === 0}
                          className="p-2 sm:p-1.5 text-neutral-500 hover:text-black active:bg-neutral-100 rounded-sm disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move up"
                          data-testid={`catalog-item-up-${p.sku}`}
                        >
                          <ArrowUp size={16} className="sm:w-3.5 sm:h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveItem(i, i + 1)}
                          disabled={i === items.length - 1}
                          className="p-2 sm:p-1.5 text-neutral-500 hover:text-black active:bg-neutral-100 rounded-sm disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move down"
                          data-testid={`catalog-item-down-${p.sku}`}
                        >
                          <ArrowDown size={16} className="sm:w-3.5 sm:h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeItem(p.sku)}
                          className="p-2 sm:p-1.5 text-neutral-500 active:text-red-600 hover:text-red-600 active:bg-red-50 rounded-sm ml-0.5 sm:ml-1"
                          title="Remove from catalog"
                          data-testid={`catalog-item-remove-${p.sku}`}
                        >
                          <Trash2 size={16} className="sm:w-3.5 sm:h-3.5" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black px-4 h-11 sm:h-10 w-full sm:w-auto"
            data-testid="edit-catalog-cancel"
          >
            <X size={14} className="inline mr-1" /> Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="btn-primary h-11 sm:h-10 w-full sm:w-auto"
            data-testid="edit-catalog-save"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save Catalog
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
